import { useState } from 'react';
import { Navigate } from 'react-router-dom';
import { Check, Eye, EyeOff, Plus, Trash2 } from 'lucide-react';
import {
  AdminPage,
  Button,
  EmptyState,
  ErrorState,
  Panel,
  Skeleton,
  StatusBadge,
  useConfirm,
} from '@/design/components';
import { usePerm } from '@/access/usePerm';
import { cn } from '@/lib/cn';
import { useEmployeeOptions } from '@/features/employees/api';
import { errorMessage } from '@/lib/errors';
import { useZoneMutations, useZones, type Zone } from './zonesApi';

const FIELD = 'min-h-11 w-full rounded-sm border border-muted bg-surface px-3 text-body text-ink';

const HINT = 'איפה בחנות עומדת היחידה';
const EXAMPLES = 'כניסה, קופות, מעבר מרכזי, מקרר אחורי';

/**
 * אזורי הפלנוגרמה (כלל 1: טבלה ומסך ניהול).
 *
 * כאן היו פעם גם "מחלקות", במובן "מה נמכר ביחידה". הן עברו
 * למחלקות של הקטלוג (מיגרציה 090), כי זה היה אותו מושג בשתי
 * רשימות נפרדות — ומי שהוסיף מחלקה במוצרים לא ראה אותה כאן.
 *
 * רשתי או של סניף: הרשת קובעת את השפה המשותפת, שבלעדיה אי אפשר
 * להשוות בין סניפים, וסניף עם מרתף יין מוסיף "מרתף" משלו.
 */
export function ZonesPage() {
  const canManage = usePerm('products.manage');
  const zones = useZones();
  const mutations = useZoneMutations();
  const confirm = useConfirm();
  const { data: options } = useEmployeeOptions();
  const branches = options?.branches ?? [];

  const [error, setError] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);

  if (!canManage) return <Navigate to="/admin" replace />;

  const run = (action: Promise<unknown>) => {
    setError(null);
    void action.catch((caught: unknown) => setError(errorMessage(caught)));
  };

  const rows = zones.data ?? [];

  return (
    <AdminPage
      title="אזורים"
      subtitle="איפה בחנות עומדת כל יחידה, כדי שהפלנוגרמה תהיה מסודרת לפי זה"
      backTo="/admin/products/planogram"
    >
      {confirm.dialog}

      {error && (
        <p role="alert" className="m-0 text-body-sm text-bad">
          {error}
        </p>
      )}

      {zones.isPending && <Skeleton className="h-48" />}
      {zones.isError && <ErrorState onRetry={() => void zones.refetch()} />}

      {zones.isSuccess && (
        <Panel
          title="אזורים"
          actions={
            isAdding ? undefined : (
              <Button variant="secondary" onClick={() => setIsAdding(true)}>
                <Plus size={18} strokeWidth={1.75} aria-hidden="true" />
                הוספת אזור
              </Button>
            )
          }
        >
          <p className="m-0 mb-3 text-body-sm text-muted">
            {HINT}. לדוגמה: {EXAMPLES}.
          </p>

          {isAdding && (
            <ZoneForm
              branches={branches}
              nextOrder={Math.max(0, ...rows.map((row) => row.sort_order)) + 1}
              onCancel={() => setIsAdding(false)}
              onSave={(input) =>
                run(mutations.save.mutateAsync(input).then(() => setIsAdding(false)))
              }
            />
          )}

          <ZoneList
            rows={rows}
            branches={branches}
            onToggle={(zone) =>
              run(mutations.setActive.mutateAsync({ id: zone.id, is_active: !zone.is_active }))
            }
            onRemove={(zone) =>
              confirm.ask({
                title: `מחיקת אזור ${zone.name}`,
                body: (
                  <p className="m-0">
                    היחידות ששויכו אליו יישארו, אבל יאבדו את הסיווג ויעברו לסוף הרשימה. אם המטרה
                    היא רק להוציא אותו מהרשימה, עדיף לכבות אותו.
                  </p>
                ),
                confirmLabel: 'מחיקת האזור',
                onConfirm: () => run(mutations.remove.mutateAsync(zone.id)),
              })
            }
          />
        </Panel>
      )}

    </AdminPage>
  );
}

function ZoneList({
  rows,
  branches,
  onToggle,
  onRemove,
}: {
  rows: Zone[];
  branches: { id: string; name: string }[];
  onToggle: (zone: Zone) => void;
  onRemove: (zone: Zone) => void;
}) {
  if (rows.length === 0) {
    return (
      <EmptyState
        title="אין עדיין אזורים"
        description={`${HINT}. בלי זה היחידות יושבות ברשימה אחת ארוכה.`}
      />
    );
  }

  return (
    <ul className="m-0 grid list-none gap-1 p-0">
      {rows.map((zone) => (
        <li
          key={zone.id}
          className="flex flex-wrap items-center gap-2 rounded-sm border border-line bg-raised px-3 py-2"
        >
          <span className="text-body font-medium text-ink">{zone.name}</span>

          {/* באילו סניפים הוא קיים — העובדה החשובה ביותר בשורה */}
          {zone.branch_ids.length === 0 ? (
            <StatusBadge tone="good">כל הרשת</StatusBadge>
          ) : (
            zone.branch_ids.map((id) => (
              <StatusBadge key={id} tone="warn">
                {branches.find((branch) => branch.id === id)?.name ?? 'סניף'}
              </StatusBadge>
            ))
          )}

          {!zone.is_active && <StatusBadge tone="bad">כבוי</StatusBadge>}

          <span className="grow" />

          <Button variant="secondary" onClick={() => onToggle(zone)}>
            {zone.is_active ? (
              <>
                <EyeOff size={16} strokeWidth={2} aria-hidden="true" />
                כיבוי
              </>
            ) : (
              <>
                <Eye size={16} strokeWidth={2} aria-hidden="true" />
                הדלקה
              </>
            )}
          </Button>
          <Button variant="secondary" onClick={() => onRemove(zone)}>
            <Trash2 size={16} strokeWidth={2} aria-hidden="true" />
            מחיקה
          </Button>
        </li>
      ))}
    </ul>
  );
}

function ZoneForm({
  branches,
  nextOrder,
  onSave,
  onCancel,
}: {
  branches: { id: string; name: string }[];
  nextOrder: number;
  onSave: (input: { branch_ids: string[]; name: string; sort_order: number }) => void;
  onCancel: () => void;
}) {
  const [name, setName] = useState('');
  const [chosen, setChosen] = useState<string[]>([]);

  return (
    <form
      className="grid gap-3 rounded-sm border border-line bg-cream p-3"
      onSubmit={(event) => {
        event.preventDefault();
        if (name.trim() === '') return;
        onSave({ branch_ids: chosen, name, sort_order: nextOrder });
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <label className="grid gap-1">
          <span className="text-label font-medium">שם האזור</span>
          <input
            autoFocus
            value={name}
            onChange={(event) => setName(event.target.value)}
            placeholder={EXAMPLES.split(',')[0]}
            className={FIELD}
          />
        </label>
      </div>

      {/*
        בחירה מרובה ולא סניף אחד: מרתף יין יש בטאגור ובמגדלי תל
        אביב ולא בשאר, ועם שדה יחיד הדרך היחידה לבטא את זה היתה
        לשכפל את אותו שם פעמיים — ואז אותו אזור הוא שתי שורות
        שצריך לערוך בנפרד.
      */}
      <fieldset className="m-0 grid gap-2 border-0 p-0">
        <legend className="p-0 text-label font-medium">
          איפה קיים{' '}
          <span className="font-normal text-muted">בלי בחירה = בכל הרשת. אפשר לבחור כמה.</span>
        </legend>

        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            aria-pressed={chosen.length === 0}
            onClick={() => setChosen([])}
            className={cn(
              'min-h-11 cursor-pointer rounded-pill border px-3 text-body-sm font-medium transition-colors',
              chosen.length === 0
                ? 'border-charcoal bg-charcoal text-on-charcoal'
                : 'border-muted bg-transparent text-ink hover:bg-cream',
            )}
          >
            כל הרשת
          </button>

          {branches.map((branch) => {
            const isOn = chosen.includes(branch.id);
            return (
              <button
                key={branch.id}
                type="button"
                aria-pressed={isOn}
                onClick={() =>
                  setChosen(isOn ? chosen.filter((id) => id !== branch.id) : [...chosen, branch.id])
                }
                className={cn(
                  'inline-flex min-h-11 cursor-pointer items-center gap-1.5 rounded-pill border px-3 text-body-sm transition-colors',
                  isOn
                    ? 'border-ink bg-olive font-semibold text-ink'
                    : 'border-muted bg-transparent font-medium text-ink hover:bg-cream',
                )}
              >
                {isOn && <Check size={16} strokeWidth={2.5} aria-hidden="true" />}
                {branch.name}
              </button>
            );
          })}
        </div>
      </fieldset>

      <p className="m-0 text-label text-muted">
        כל הרשת הוא ברירת המחדל, כי רק שפה משותפת מאפשרת להשוות בין סניפים. בוחרים סניפים כשהאזור
        באמת אינו קיים אצל האחרים.
      </p>

      <div className="flex flex-wrap gap-2">
        <Button type="submit" variant="primary" disabled={name.trim() === ''}>
          הוספת האזור
        </Button>
        <Button type="button" variant="secondary" onClick={onCancel}>
          ביטול
        </Button>
      </div>
    </form>
  );
}
